import {Button, SmallLoader} from '@kit';

import React from 'react';

import {Spell, useSpellQuery, ImageTarget} from '../../../generated/types';
import {PageTools} from '../../../components/page-tools';
import {getApolloError, humanJoin, humanEnumeration, getSourceText, wordByCount} from '../../../components/utils';
import {EditHoc} from '../../../components/edit-hoc';
import {Link, buildUrl} from '../../../lib/routes';
import {spellSchoolToString} from '../../../components/enums';
import {Gallery} from '../../../components/gallery';
import {OtherTitles} from '../../../components/other-titles';
import {NotFoundSeo, isNotFoundError, useNotFound} from '../../../lib/seo';
import {SpellSeo} from '../seo';
import {NotFound} from '../../../components/not-found';
import {SpellDetails} from './spell-details';
import {CommentTargetType, Comments} from '../../../components/comments';

import {PageTitle} from '../../../components/page-title';
import dashboardCss from '@kit/sidebar/dashboard.module.css';
import pageCss from '../../../components/common.module.css';
import markdownCss from '../../../components/utils/md/markdown.module.css';
import css from './spell-view.module.css';

type Props = {
	baseId: string;
	variantId?: string;
	title: string;
};

export const SpellView: React.FC<Props> = ({baseId, variantId, title}) => {
	const {data: baseData, loading: baseLoading, error} = useSpellQuery({variables: {id: baseId}});
	const {data: variantData, loading: variantLoading} = useSpellQuery({
		variables: {id: variantId!},
		skip: !variantId,
	});

	const baseEntry = baseData?.spell;
	const spell = variantId ? variantData?.spell : baseData?.spell;
	const loading = baseLoading || variantLoading;
	const displayTitle = (variantId ? variantData?.spell?.title : null) || title;
	const legacySpell = baseEntry?.legacySpell;

	const notFound = !loading && !spell && isNotFoundError(error);
	useNotFound(notFound);

	return (
		<article className={dashboardCss.readingWrapper}>
			{spell && (
				<SpellSeo
					spell={spell as Spell}
					path={variantId ? buildUrl('spellVariant', {id: baseId, variantId}) : undefined}
				/>
			)}
			{notFound && <NotFoundSeo />}
			<PageTools />
			<PageTitle
				subTitle={
					spell && (
						<em>
							{spell.level ? `${spell.level}-й круг` : 'Фокус'}, {spellSchoolToString(spell.school)}
							{/*TODO: классы*/}
						</em>
					)
				}
			>
				{displayTitle}
			</PageTitle>
			<div className={dashboardCss.page}>
				{legacySpell && (
					<div className={css.sourceToggle}>
						<div className={css.label}>Редакция</div>
						<div>
							<Button
								shape={'circular'}
								size={'small'}
								route={'spell'}
								routeParams={{id: baseId}}
								checked={!variantId}
							>
								Новая
							</Button>
						</div>
						<div>
							<Button
								shape={'circular'}
								size={'small'}
								route={'spellVariant'}
								routeParams={{id: baseId, variantId: legacySpell.id}}
								checked={!!variantId}
							>
								Старая
							</Button>
						</div>
					</div>
				)}
				{loading && (
					<div className={pageCss.fullSizeBlock}>
						<SmallLoader />
					</div>
				)}
				{notFound ? (
					<NotFound />
				) : (
					error && <div className={pageCss.fullSizeBlock}>{getApolloError(error).message}</div>
				)}
				{spell && (
					<div>
						<EditHoc
							load={() => import('../edit-buttons').then((m) => m.EditSpell)}
							editProps={{id: spell.id}}
						>
							<SpellDetails spell={spell as Spell} />
							<div className={markdownCss.margin}>
								<div className={markdownCss.indent}>
									<OtherTitles
										originalTitle={spell.originalTitle}
										alternativeTitles={spell.alternativeTitles}
									/>
									<p>
										<b>Источник: </b>
										{getSourceText(spell.source as any)}.
									</p>
								</div>
							</div>
						</EditHoc>
						<Gallery
							images={spell.images || []}
							target={variantId ? undefined : {type: ImageTarget.Spell, id: baseId}}
						/>
					</div>
				)}
				{spell && <Comments targetType={CommentTargetType.Spell} targetId={baseId} />}
			</div>
		</article>
	);
};
